import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Construction } from 'lucide-react';
import { PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { findReport, isReportAvailable, reportName } from '@/features/reports/catalogue';
import { ReportPage } from '@/features/reports/report-page';
import { parseSettings, type Settings } from '@/features/reports/settings';
import { ltr } from '@/lib/arabic';
import { useLanguage, useT } from '@/lib/i18n';

export const Route = createFileRoute('/reports/$reportKey')({
  component: ReportRoute,
  validateSearch: (s: Record<string, unknown>): Settings => parseSettings(s),
});

/** Screen R2 (document 06): any report in the catalogue, through the shared report page. */
function ReportRoute() {
  const { reportKey } = Route.useParams();
  const settings = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const t = useT();
  const lang = useLanguage();
  const spec = findReport(reportKey);
  if (!spec || !isReportAvailable(reportKey)) {
    return (
      <>
        <PageHeader title={spec ? reportName(spec) : t('reportPages.reports.route.notFound')} />
        <div className="flex flex-col items-center justify-center rounded-lg border bg-card px-6 py-16 text-center">
          {spec ? (
            <>
              <Construction className="mb-3 size-8 text-primary" aria-hidden />
              <p className="text-base font-medium" data-testid="not-available">
                {t('reportPages.reports.route.notAvailable')}
              </p>
            </>
          ) : (
            <p className="text-base font-medium">
              {t('reportPages.reports.route.noSuchReport', {
                key: lang === 'ar' ? ltr(reportKey) : reportKey,
              })}
            </p>
          )}
          <Button asChild className="mt-6">
            <Link to="/reports">{t('reportPages.reports.route.backToList')}</Link>
          </Button>
        </div>
      </>
    );
  }
  return (
    <ReportPage
      key={reportKey}
      spec={spec}
      settings={settings}
      onRun={(next) => void navigate({ search: next })}
    />
  );
}
